import { type FB, GQL } from "@binale-tech/shared";

import {
    type GenericRecord,
    RecordBank,
    RecordDeb,
    RecordER,
    RecordERAnzahlung,
    RecordFE,
    RecordKB,
    RecordLA,
    RecordPOS,
} from "@domain/models/GenericRecord";
import { readFirebaseRecord } from "@domain/models/converters/firebaseReaders";
import type { IGenericRecord } from "@domain/models/Interfaces";
import { type VatProvenance } from "./vatPeriods";

/**
 * The comparable shape of one booking, built from either side of the comparison: the
 * frozen snapshot or the live record. Keeping the diff on this projection rather than on
 * GenericRecord keeps the function pure and the fixtures trivial.
 */
export type VatDiffSubject = {
    brutto: number;
    bu: string;
    period: number;
    num: string | null;
    cancellation: string | null;
};

export type StoredSnapshot = {
    recordId: string;
    product: GQL.IProductKey;
    subject: VatDiffSubject;
    /** The booking as it was filed, rehydrated. The only copy once the record is deleted. */
    record: GenericRecord;
    contributions: { kz: number; isBmg: boolean; amount: number }[];
};

const RECORD_BY_PRODUCT: Partial<Record<GQL.IProductKey, new (data: IGenericRecord) => GenericRecord>> = {
    [GQL.IProductKey.Er]: RecordER,
    [GQL.IProductKey.ErA]: RecordERAnzahlung,
    [GQL.IProductKey.Fe]: RecordFE,
    [GQL.IProductKey.Pos]: RecordPOS,
    [GQL.IProductKey.La]: RecordLA,
    [GQL.IProductKey.Deb]: RecordDeb,
    [GQL.IProductKey.Bank]: RecordBank,
    [GQL.IProductKey.Kb]: RecordKB,
};

export type VatDeviationCause =
    /** An override entered before transmission. It carries no reason: the Kz cell is not
     *  editable today, and a free-text justification was never asked for. */
    | { type: "manual" }
    | { type: "added" }
    /** A Storno counterweight. Arrives as a new booking, but calling it one is misleading. */
    | { type: "storno" }
    | { type: "removed" }
    | { type: "changed"; fieldDiffs: { field: keyof VatDiffSubject; from: unknown; to: unknown }[] };

export type VatDeviationEntry = {
    recordKey: string | null;
    label: string;
    amount: number;
    cause: VatDeviationCause;
};

export type VatKennzahlDeviation = {
    kz: number;
    isBmg: boolean;
    filed: number;
    current: number;
    delta: number;
    entries: VatDeviationEntry[];
};

/**
 * The live side of the comparison. Whether it is populated is stated by the loader and
 * never derived from the records themselves: a year with no bookings and a year that has
 * not loaded look identical in the data, and guessing wrong reports the whole filing as
 * deleted. The filed year is always the selected year and the record window always spans
 * it, so being loaded is the only thing left to establish.
 */
export type VatRecordCoverage = { isLoaded: boolean };

export type VatReconciliation = { status: "ok"; deviations: VatKennzahlDeviation[] } | { status: "recordsNotLoaded" };

const DIFF_FIELDS: readonly (keyof VatDiffSubject)[] = ["brutto", "bu", "period", "num", "cancellation"];

/**
 * Derives the comparable shape. BOTH sides of the diff must go through this: if the
 * snapshot and the live record were normalised even slightly differently, every record
 * would show up as `changed` with a phantom field diff.
 */
export const buildDiffSubject = (input: {
    items: { brutto?: number | null; bu?: number | null }[];
    period: number;
    num?: string | null;
    cancellation?: string | null;
}): VatDiffSubject => ({
    brutto: input.items.reduce((acc, i) => acc + (i.brutto ?? 0), 0),
    bu: [...new Set(input.items.map((i) => i.bu ?? 0))].sort((a, b) => a - b).join(","),
    period: input.period,
    num: input.num ?? null,
    cancellation: input.cancellation ?? null,
});

/**
 * Rehydrates one stored snapshot row into a real record.
 *
 * The backend freezes the booking in the shape Firebase holds it, items included, so the
 * same constructors the live records go through rebuild it here and a booking deleted since
 * the filing behaves exactly like a live one. Nothing is re-derived field by field.
 */
export const parseStoredSnapshot = (row: GQL.IElsterRecordSnapshot): StoredSnapshot => {
    const Ctor = RECORD_BY_PRODUCT[row.product] ?? RecordFE;
    const stored: FB.Record = JSON.parse(row.record);
    const record = new Ctor(readFirebaseRecord(stored));
    return {
        recordId: row.recordId,
        product: row.product,
        subject: buildDiffSubject({
            items: record.items ?? [],
            period: record.period ?? 0,
            num: record.num,
            cancellation: record.cancellation,
        }),
        record,
        contributions: row.contributions.map((c) => ({ kz: c.kz, isBmg: c.isBmg, amount: c.amount })),
    };
};

/** Filing periods: 1-12 are months, 41-44 are quarters covering three months each. */
export const periodsCoveredBy = (filingPeriod: number): number[] =>
    filingPeriod > 40 ? [1, 2, 3].map((n) => (filingPeriod - 41) * 3 + n) : [filingPeriod];

/**
 * Whether one filing accounts for everything the selected view shows, comparing the months
 * behind both rather than the period codes: a quarter code is never one of its own months,
 * so a quarterly filing would otherwise fail to match the quarter view it produced.
 *
 * Deliberately one-directional. Three monthly filings do not jointly cover a quarter view,
 * and reconciling that view against any one of them would report the other two months'
 * bookings as drift.
 */
export const filingCovers = (filingPeriod: number, viewPeriod: number): boolean => {
    const filed = periodsCoveredBy(filingPeriod);
    return periodsCoveredBy(viewPeriod).every((p) => filed.includes(p));
};

/**
 * Whether a filing recorded what it transmitted, i.e. whether reconciling against it is
 * possible at all. kzValues is written for every Kennzahl at filing time, zeros included,
 * so an empty array means the filing predates breakdown recording, never that it was filed
 * as a Nullmeldung. The distinction matters because an absent baseline is not a baseline of
 * zero: reconcileVatFiling against nothing reports every current figure as newly booked.
 */
export const hasFiledBaseline = (filing: { kzValues?: GQL.IElsterKzValue[] | null } | null | undefined): boolean =>
    Boolean(filing?.kzValues?.length);

const groupKey = (kz: number, isBmg: boolean) => `${kz}:${isBmg ? "bmg" : "st"}`;

const diffFields = (
    before: VatDiffSubject | undefined,
    after: VatDiffSubject | undefined,
): { field: keyof VatDiffSubject; from: unknown; to: unknown }[] => {
    if (!before || !after) {
        return [];
    }
    return DIFF_FIELDS.filter((f) => before[f] !== after[f]).map((f) => ({
        field: f,
        from: before[f],
        to: after[f],
    }));
};

/**
 * Explains why a filed UStVA differs from what would be transmitted now, per Kennzahl.
 *
 * Sign rule, applied everywhere: an entry's `amount` is its contribution to
 * `delta = current - filed`, where `filed` is the snapshot sum plus the manual override
 * delta - that being what actually went to the Finanzamt. So an override that lowered the
 * filing contributes a positive amount to today's difference. `sum(entries) === delta`
 * always holds, which is what stops a cause being silently dropped or double-counted.
 *
 * Everything here is integer cents on both sides, so there is no rounding anywhere. If
 * you reach for Math.round in this file, a euro boundary has leaked in upstream.
 */
export const reconcileVatFiling = (
    snapshot: StoredSnapshot[],
    kzValues: GQL.IElsterKzValue[],
    provenance: VatProvenance,
    currentSubjects: Map<string, VatDiffSubject>,
    periods: number[],
    coverage: VatRecordCoverage,
): VatReconciliation => {
    // Required, not optional: a caller that forgets this reports every filed record as
    // removed, which is the most alarming wrong answer this file can produce.
    if (!coverage.isLoaded) {
        return { status: "recordsNotLoaded" };
    }

    const snapshotByRecord = new Map(snapshot.map((s) => [s.recordId, s]));

    const filedByGroup = new Map<string, Map<string, number>>();
    for (const row of snapshot) {
        for (const c of row.contributions) {
            const g = groupKey(c.kz, c.isBmg);
            const byRecord = filedByGroup.get(g) ?? new Map<string, number>();
            byRecord.set(row.recordId, (byRecord.get(row.recordId) ?? 0) + c.amount);
            filedByGroup.set(g, byRecord);
        }
    }

    const currentByGroup = new Map<string, Map<string, number>>();
    for (const [key, contributions] of provenance) {
        const [periodStr, route, kind] = key.split(":");
        // X-routes are derived from other Kennzahlen, never summed from records
        if (!periods.includes(Number(periodStr)) || !route || route.startsWith("X")) {
            continue;
        }
        const g = groupKey(Number(route), kind === "bmg");
        const byRecord = currentByGroup.get(g) ?? new Map<string, number>();
        for (const c of contributions) {
            byRecord.set(c.recordKey, (byRecord.get(c.recordKey) ?? 0) + c.amount);
        }
        currentByGroup.set(g, byRecord);
    }

    const manualByKz = new Map(kzValues.filter((v) => v.isManual).map((v) => [v.kz, v]));
    const labelOf = (recordKey: string): string =>
        currentSubjects.get(recordKey)?.num ?? snapshotByRecord.get(recordKey)?.subject.num ?? recordKey;

    const result: VatKennzahlDeviation[] = [];
    for (const g of new Set([...filedByGroup.keys(), ...currentByGroup.keys()])) {
        const [kzStr, kindStr] = g.split(":");
        const kz = Number(kzStr);
        const isBmg = kindStr === "bmg";
        const filedRecords = filedByGroup.get(g) ?? new Map<string, number>();
        const currentRecords = currentByGroup.get(g) ?? new Map<string, number>();
        const entries: VatDeviationEntry[] = [];

        // An override applies to the St side of a Kennzahl, never the Bemessungsgrundlage.
        // Both operands are cents, so this is exact subtraction, not rounding.
        const manual = isBmg ? undefined : manualByKz.get(kz);
        const manualDelta = manual ? manual.amount - manual.computed : 0;
        if (manual && manualDelta !== 0) {
            entries.push({
                recordKey: null,
                label: "",
                amount: -manualDelta,
                cause: { type: "manual" },
            });
        }

        for (const recordKey of new Set([...filedRecords.keys(), ...currentRecords.keys()])) {
            const before = filedRecords.get(recordKey);
            const after = currentRecords.get(recordKey);
            const label = labelOf(recordKey);
            if (before === undefined) {
                // A German Storno books a counterweight rather than deleting anything, so it
                // reaches this branch as a new record with a negative amount. Calling that
                // "booked after the transmission" describes the mechanism and hides the fact.
                const isStorno = currentSubjects.get(recordKey)?.cancellation === "counterweight";
                entries.push({
                    recordKey,
                    label,
                    amount: after!,
                    cause: { type: isStorno ? "storno" : "added" },
                });
            } else if (after === undefined) {
                entries.push({ recordKey, label, amount: -before, cause: { type: "removed" } });
            } else if (before !== after) {
                entries.push({
                    recordKey,
                    label,
                    amount: after - before,
                    cause: {
                        type: "changed",
                        fieldDiffs: diffFields(
                            snapshotByRecord.get(recordKey)?.subject,
                            currentSubjects.get(recordKey),
                        ),
                    },
                });
            }
        }

        const filed = [...filedRecords.values()].reduce((acc, v) => acc + v, 0) + manualDelta;
        const current = [...currentRecords.values()].reduce((acc, v) => acc + v, 0);
        result.push({ kz, isBmg, filed, current, delta: current - filed, entries });
    }
    return { status: "ok", deviations: result };
};
